// Vendor Portal — Registration fee payment. Two-tab layout:
//   1. Online (CCAvenue) — the existing hosted-payment flow
//   2. Bank Transfer — vendor NEFTs to the Trust bank account and uploads
//      a receipt + UTR; a Trust admin then verifies the credit and marks
//      the registration paid.

import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, CreditCard, AlertTriangle, CheckCircle2, ChevronRight, Building2,
  Upload, Copy, ClipboardCheck, Clock, XCircle,
} from "lucide-react";
import vendorApi from "@/lib/vendorApi";
import { useVendor } from "@/pages/vendor/VendorLayout";

function submitToCCAvenue({ gateway_url, access_code, enc_request }) {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = gateway_url;
  for (const [name, value] of [["encRequest", enc_request], ["access_code", access_code]]) {
    const input = document.createElement("input");
    input.type = "hidden"; input.name = name; input.value = value;
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
}

export default function VendorPayment() {
  const { payment, reload, mustChange } = useVendor();
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("online");

  const feeAmount = payment?.fee?.amount ?? 0;
  const feeWaived = !!payment?.fee?.is_waived;
  const isPaid = !!payment?.is_paid;
  const docsComplete = !!payment?.documents_complete;
  const latest = payment?.latest_order || null;
  const pendingOffline = latest && latest.gateway === "bank_transfer" && (latest.status === "PENDING_VERIFICATION");

  const startPayment = async () => {
    setBusy(true);
    try {
      const { data } = await vendorApi.post("/vendor/payments/initiate");
      submitToCCAvenue(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not start payment");
      setBusy(false);
    }
  };

  const confirmWaived = async () => {
    setBusy(true);
    try {
      await vendorApi.post("/vendor/payments/waive-confirm");
      toast.success("Registration marked as fee-cleared");
      await reload();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not confirm");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-5 md:p-8 max-w-3xl mx-auto" data-testid="vendor-payment-card">
      <section className="bg-white border border-[#E9E2D2] rounded-2xl p-6 md:p-8 shadow-sm">
        <div className="flex items-center gap-2 text-[#0D5C63]">
          <CreditCard size={20} />
          <h1 className="trust-serif text-2xl">Vendor Registration Fee</h1>
        </div>

        {isPaid ? (
          <PaidBlock payment={payment} />
        ) : feeWaived ? (
          <WaivedBlock busy={busy} docsComplete={docsComplete} onConfirm={confirmWaived} />
        ) : !payment?.fee || feeAmount <= 0 ? (
          <div className="mt-4 text-[11px] text-[#8a8478]">
            Registration fee is not yet configured. Please check back later.
          </div>
        ) : (
          <>
            {/* Amount headline */}
            <div className="mt-6 bg-[#FAFAF7] border border-[#E9E2D2] rounded-xl p-5">
              <div className="text-[10px] uppercase tracking-widest text-[#8a8478]">Amount payable</div>
              <div className="font-serif text-4xl text-[#0D5C63] mt-1" data-testid="vendor-payment-amount">
                ₹ {Number(feeAmount).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
              </div>
              <div className="text-[11px] text-[#8a8478] mt-1">
                One-time empanelment fee. Choose your preferred payment method below.
              </div>
            </div>

            {!docsComplete && (
              <div
                className="mt-4 inline-flex items-start gap-1.5 text-[12px] text-[#8a5a1a] bg-[#F5EFE3] border border-[#c78e42]/40 px-3 py-2 rounded"
                data-testid="vendor-payment-docs-warning"
              >
                <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                <span>Please upload all six document sets before paying the fee.
                  <NavLink to="/vendor/portal/documents" className="ml-1 inline-flex items-center gap-0.5 font-bold underline text-[#0D5C63]">
                    Go to documents <ChevronRight size={11} />
                  </NavLink>
                </span>
              </div>
            )}

            {/* Pending offline transfer notice */}
            {pendingOffline && (
              <div
                className="mt-4 bg-[#FEF9E7] border border-[#c78e42]/40 rounded-lg p-4 text-sm"
                data-testid="vendor-offline-pending"
              >
                <div className="flex items-center gap-2 text-[#8a5a1a] font-semibold">
                  <Clock size={15} /> Awaiting verification
                </div>
                <div className="text-[#3E3E3E] mt-1.5">
                  Your bank-transfer submission (<span className="font-mono text-xs">{latest.order_id}</span>) is being reviewed by GS Mandal.
                  UTR: <span className="font-mono">{latest.utr}</span>. You&apos;ll get a payment-receipt email once verified.
                </div>
              </div>
            )}

            {latest?.status === "REJECTED" && latest.gateway === "bank_transfer" && (
              <div className="mt-4 bg-[#FEEBEB] border border-[#B02020]/30 rounded-lg p-4 text-sm" data-testid="vendor-offline-rejected">
                <div className="flex items-center gap-2 text-[#B02020] font-semibold">
                  <XCircle size={15} /> Previous submission rejected
                </div>
                {latest.rejection_reason && (
                  <div className="text-[#3E3E3E] mt-1.5">Reason: <b>{latest.rejection_reason}</b></div>
                )}
                <div className="text-[#3E3E3E] mt-1">Please re-check your transfer details and submit again.</div>
              </div>
            )}

            {/* Tabs */}
            <div className="mt-6 flex gap-1 border-b border-[#E9E2D2]" role="tablist">
              <TabButton
                active={tab === "online"}
                onClick={() => setTab("online")}
                icon={<CreditCard size={14} />}
                label="Online (CCAvenue)"
                testid="vendor-pay-tab-online"
              />
              <TabButton
                active={tab === "bank"}
                onClick={() => setTab("bank")}
                icon={<Building2 size={14} />}
                label="Bank Transfer"
                testid="vendor-pay-tab-bank"
              />
            </div>

            <div className="pt-5">
              {tab === "online" ? (
                <OnlineTab
                  busy={busy}
                  disabled={busy || !docsComplete || mustChange || pendingOffline}
                  onPay={startPayment}
                  pendingOffline={pendingOffline}
                />
              ) : (
                <BankTransferTab
                  amount={feeAmount}
                  disabled={!docsComplete || mustChange || pendingOffline}
                  onSubmitted={async () => { await reload(); setTab("bank"); }}
                  pendingOffline={pendingOffline}
                />
              )}
            </div>

            {latest?.status && latest.status !== "SUCCESS" && latest.gateway === "ccavenue" && !isPaid && (
              <div className="mt-4 text-[11px] text-[#8a8478]">
                Last online attempt: <span className="font-mono">{latest.order_id}</span> — status <b className="text-[#B02020]">{latest.status}</b>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function TabButton({ active, onClick, icon, label, testid }) {
  return (
    <button
      type="button"
      role="tab"
      onClick={onClick}
      aria-selected={active}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors ${
        active
          ? "border-[#F26419] text-[#0D5C63] bg-white"
          : "border-transparent text-[#8a8478] hover:text-[#0D5C63]"
      }`}
    >
      {icon} {label}
    </button>
  );
}

function OnlineTab({ busy, disabled, onPay, pendingOffline }) {
  return (
    <div data-testid="vendor-pay-tab-online-panel">
      <p className="text-sm text-[#3E3E3E] leading-relaxed">
        Pay securely with a debit card, credit card, UPI, wallet, or net banking through CCAvenue.
        You&apos;ll be redirected to CCAvenue and returned once the payment completes.
      </p>
      <button
        type="button"
        onClick={onPay}
        disabled={disabled}
        data-testid="vendor-pay-now"
        className="mt-5 inline-flex items-center gap-2 bg-gradient-to-r from-[#F26419] to-[#D90368] hover:from-[#D90368] hover:to-[#F26419] text-white text-xs font-bold uppercase tracking-wider px-6 py-3 rounded-full shadow-lg shadow-[#F26419]/25 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {busy ? <><Loader2 size={14} className="animate-spin" /> Opening…</> : <><CreditCard size={14} /> Pay Registration Fee</>}
      </button>
      {pendingOffline && (
        <div className="mt-3 text-[11px] text-[#8a8478]">
          Online payment is disabled while a bank-transfer submission is pending verification.
        </div>
      )}
    </div>
  );
}

function BankTransferTab({ amount, disabled, onSubmitted, pendingOffline }) {
  const [bank, setBank] = useState(null);
  const [loadingBank, setLoadingBank] = useState(true);
  const [utr, setUtr] = useState("");
  const [remarks, setRemarks] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await vendorApi.get("/vendor/payments/trust-bank");
        setBank(data);
      } catch (err) {
        const d = err?.response?.data?.detail;
        toast.error(typeof d === "string" ? d : "Could not load bank details");
      } finally { setLoadingBank(false); }
    })();
  }, []);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return setFile(null);
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Receipt must be under 5 MB");
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!utr.trim() || utr.trim().length < 6) {
      return toast.error("Enter a valid UTR / transaction reference (min 6 chars)");
    }
    if (!file) {
      return toast.error("Please attach the bank transfer receipt");
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("utr", utr.trim());
      fd.append("remarks", remarks.trim());
      fd.append("file", file);
      await vendorApi.post("/vendor/payments/offline", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Transfer submitted. GS Mandal will verify and confirm shortly.");
      setUtr(""); setRemarks(""); setFile(null);
      await onSubmitted();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Submission failed");
    } finally { setSubmitting(false); }
  };

  if (loadingBank) {
    return (
      <div className="text-sm text-[#8a8478] flex items-center gap-2" data-testid="vendor-pay-tab-bank-panel">
        <Loader2 size={14} className="animate-spin" /> Loading bank details…
      </div>
    );
  }
  if (!bank) {
    return (
      <div className="text-sm text-[#8a5a1a] bg-[#F5EFE3] border border-[#c78e42]/40 px-3 py-2 rounded">
        Bank account details are not yet available. Please contact GS Mandal or use the online option.
      </div>
    );
  }

  return (
    <div data-testid="vendor-pay-tab-bank-panel">
      <p className="text-sm text-[#3E3E3E] leading-relaxed">
        Transfer <b>₹ {Number(amount).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</b>{" "}
        via NEFT / RTGS / IMPS to the account below, then submit the UTR and receipt.
        GS Mandal will verify the credit and email you an official payment receipt.
      </p>

      {/* Bank details */}
      <div className="mt-4 bg-[#FAFAF7] border border-[#E9E2D2] rounded-xl overflow-hidden" data-testid="vendor-bank-details">
        <div className="bg-[#0D5C63] text-white px-4 py-2.5 flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold">
          <Building2 size={14} /> Beneficiary Bank
        </div>
        <div className="divide-y divide-[#E9E2D2]">
          <BankRow label="Beneficiary" value={bank.beneficiary_name} testid="vendor-bank-beneficiary" onCopy={copy} />
          <BankRow label="Bank" value={bank.bank_name} testid="vendor-bank-name" onCopy={copy} />
          {bank.branch && <BankRow label="Branch" value={bank.branch} testid="vendor-bank-branch" onCopy={copy} />}
          <BankRow label="Account No." value={bank.account_number} testid="vendor-bank-account" onCopy={copy} mono />
          {bank.ifsc && <BankRow label="IFSC" value={bank.ifsc} testid="vendor-bank-ifsc" onCopy={copy} mono />}
          {bank.account_type && <BankRow label="Type" value={bank.account_type} testid="vendor-bank-type" onCopy={copy} />}
        </div>
      </div>

      {/* Submission form */}
      <form onSubmit={onSubmit} className="mt-5 space-y-4" data-testid="vendor-bank-form">
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-[#8a8478] font-bold mb-1">
            UTR / Transaction Reference *
          </label>
          <input
            type="text"
            value={utr}
            onChange={(e) => setUtr(e.target.value)}
            required
            minLength={6}
            maxLength={64}
            data-testid="vendor-bank-utr"
            placeholder="e.g. HDFCN12345678901"
            className="w-full border border-[#E9E2D2] rounded-md px-3 py-2 text-sm font-mono focus:border-[#0D5C63] focus:ring-1 focus:ring-[#0D5C63] outline-none"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-[#8a8478] font-bold mb-1">
            Remarks (optional)
          </label>
          <textarea
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            maxLength={500}
            data-testid="vendor-bank-remarks"
            rows={2}
            placeholder="Any additional information for GS Mandal"
            className="w-full border border-[#E9E2D2] rounded-md px-3 py-2 text-sm focus:border-[#0D5C63] focus:ring-1 focus:ring-[#0D5C63] outline-none"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-[#8a8478] font-bold mb-1">
            Bank Transfer Receipt (PDF / JPG / PNG, up to 5 MB) *
          </label>
          <label
            className="flex items-center gap-3 border-2 border-dashed border-[#E9E2D2] rounded-lg px-4 py-4 cursor-pointer hover:border-[#0D5C63] transition-colors"
            data-testid="vendor-bank-file-label"
          >
            <Upload size={16} className="text-[#0D5C63]" />
            <span className="text-sm text-[#3E3E3E]">
              {file ? file.name : "Click to select receipt file…"}
            </span>
            <input
              type="file"
              accept=".pdf,image/png,image/jpeg"
              onChange={onFileChange}
              className="hidden"
              data-testid="vendor-bank-file"
            />
          </label>
        </div>
        <button
          type="submit"
          disabled={disabled || submitting || !utr.trim() || !file}
          data-testid="vendor-bank-submit"
          className="inline-flex items-center gap-2 bg-[#0D5C63] hover:bg-[#083E43] text-white text-xs font-bold uppercase tracking-wider px-6 py-3 rounded-full shadow disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <><Loader2 size={14} className="animate-spin" /> Submitting…</>
          ) : (
            <><ClipboardCheck size={14} /> Submit Transfer Details</>
          )}
        </button>
        {pendingOffline && (
          <div className="text-[11px] text-[#8a8478]">
            A previous bank-transfer submission is pending verification. Wait for the outcome before submitting again.
          </div>
        )}
      </form>
    </div>
  );
}

function BankRow({ label, value, testid, onCopy, mono = false }) {
  if (!value) return null;
  return (
    <div className="px-4 py-2.5 flex items-center justify-between gap-3">
      <div className="text-[10px] uppercase tracking-widest text-[#8a8478] font-bold shrink-0 w-24">{label}</div>
      <div className={`flex-1 text-sm text-[#0D5C63] ${mono ? "font-mono" : "font-semibold"}`} data-testid={testid}>
        {value}
      </div>
      <button
        type="button"
        onClick={() => onCopy(value, label)}
        className="text-[#8a8478] hover:text-[#0D5C63] p-1"
        aria-label={`Copy ${label}`}
        data-testid={`${testid}-copy`}
      >
        <Copy size={13} />
      </button>
    </div>
  );
}

function PaidBlock({ payment }) {
  return (
    <div className="mt-5 bg-[#F0F7F2] border border-[#0f7a45]/25 rounded-lg p-5" data-testid="vendor-payment-paid">
      <div className="inline-flex items-center gap-2 bg-[#0f7a45]/10 text-[#0f7a45] text-xs font-bold px-3 py-1.5 rounded-full">
        <CheckCircle2 size={13} /> Fee cleared
      </div>
      <div className="mt-4 grid gap-2 text-sm text-[#3E3E3E]">
        {payment.paid_at && <div>Paid on: <b>{new Date(payment.paid_at).toLocaleString()}</b></div>}
        {payment.payment_order_id && payment.payment_order_id !== "WAIVED" && (
          <div>Order ID: <span className="font-mono text-xs">{payment.payment_order_id}</span></div>
        )}
        {payment.payment_tracking_id && (
          <div>Tracking / UTR: <span className="font-mono text-xs">{payment.payment_tracking_id}</span></div>
        )}
        {payment.payment_order_id === "WAIVED" && (
          <div className="text-[#8a8478] italic">Registration fee was waived by GS Mandal.</div>
        )}
      </div>
    </div>
  );
}

function WaivedBlock({ busy, docsComplete, onConfirm }) {
  return (
    <div className="mt-5 bg-[#F5EFE3] border border-[#F26419]/40 rounded-lg p-5">
      <div className="text-sm text-[#3E3E3E]">
        GS Mandal has currently <b>waived</b> the vendor registration fee. Click below to confirm and finalise your empanelment.
      </div>
      <button
        type="button"
        onClick={onConfirm}
        disabled={busy || !docsComplete}
        data-testid="vendor-confirm-waived"
        className="mt-4 inline-flex items-center gap-2 bg-[#0D5C63] hover:bg-[#083E43] text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-full disabled:opacity-60"
      >
        {busy ? <><Loader2 size={13} className="animate-spin" /> Confirming…</> : <><CheckCircle2 size={13} /> Confirm & Finalise</>}
      </button>
    </div>
  );
}
